import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { MessageCircle, Search, UserRound } from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const formatDateTimeBR = (value) => {
  if (!value) return '';

  const raw = String(value);
  const normalized =
    raw.endsWith('Z') || raw.includes('+') || raw.includes('-03:00')
      ? raw
      : `${raw}Z`;

  return new Date(normalized).toLocaleString('pt-BR', {
    timeZone: 'America/Recife',
  });
};

const WhatsAppBusinessPage = () => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [search, setSearch] = useState('');

  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const [windowOpen, setWindowOpen] = useState(false);
  const [windowExpiresAt, setWindowExpiresAt] = useState(null);

  const authHeaders = {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  };

  const loadChats = async () => {
    try {
      setLoadingChats(true);
      const response = await axios.get(`${API_URL}/webhooks/whatsapp/chats`, authHeaders);
      setChats(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar conversas WhatsApp:', error);
    } finally {
      setLoadingChats(false);
    }
  };

  const loadMessages = async (chat, options = {}) => {
    try {
      if (!options.silent) {
      setSelectedChat(chat);
      }
      if (!options.silent) {
      setLoadingMessages(true);
      }

      const response = await axios.get(
        `${API_URL}/webhooks/whatsapp/chats/${encodeURIComponent(chat.id)}/messages`,
        authHeaders
      );

      setSelectedChat(response.data?.chat || chat);
      setMessages(Array.isArray(response.data?.messages) ? response.data.messages : []);
      setWindowOpen(response.data?.window_open === true);
      setWindowExpiresAt(response.data?.window_expires_at || null);

      await loadChats();
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    loadChats();

    const interval = setInterval(() => {
      loadChats();

      if (selectedChat?.id) {
      loadMessages(selectedChat, { silent: true });
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [selectedChat?.id]);

  const filteredChats = chats.filter((chat) => {
    const term = search.toLowerCase();

    return (
      (chat.patient_name || '').toLowerCase().includes(term) ||
      (chat.contact_name || '').toLowerCase().includes(term) ||
      (chat.phone || '').includes(term) ||
      (chat.last_message || '').toLowerCase().includes(term)
    );
  });

  return (
    <Layout>
      <div className="h-[calc(100vh-120px)] bg-white border border-gray-200 rounded-xl overflow-hidden flex">
        <div className="w-full md:w-80 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              WhatsApp Business
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Mensagens recebidas pelo número da plataforma.
            </p>

            <div className="mt-3 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar conversa..."
                className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
              />
            </div>

            <button
              type="button"
              onClick={loadChats}
              className="mt-3 w-full inline-flex items-center justify-center px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
            >
              Atualizar conversas
            </button>

          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingChats ? (
              <p className="p-4 text-sm text-gray-500">Carregando conversas...</p>
            ) : filteredChats.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">Nenhuma conversa recebida ainda.</p>
            ) : (
              filteredChats.map((chat) => {
                const active = selectedChat?.id === chat.id;

                return (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => loadMessages(chat)}
                    className={`w-full text-left p-4 border-b hover:bg-gray-50 ${
                      active ? 'bg-green-50' : 'bg-white'
                    }`}
                  >
                    <div className="flex justify-between gap-2">
                      <div>
                        <div
                          className={`font-medium text-sm truncate ${
                            chat.unread_count > 0 ? 'text-gray-950 font-semibold' : 'text-gray-900'
                          }`}
                        >
                          {chat.patient_name || chat.contact_name || 'Contato não identificado'}
                        </div>

                        {chat.phone && (
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {chat.phone}
                          </div>
                        )}

                        {chat.conversation_origin === 'multiple_patients_same_user' && (
                          <div className="text-[11px] text-amber-700 mt-0.5 leading-tight">
                            Telefone compartilhado, usado por mais de um paciente
                          </div>
                        )}

                        {chat.conversation_origin === 'assigned_from_admin' && (
                          <div className="text-[11px] text-blue-700 mt-0.5 leading-tight">
                            Encaminhada pelo Admin do OdontoAssist AI
                          </div>
                        )}

                      </div>

                      {chat.unread_count > 0 && (
                        <span className="bg-green-600 text-white text-xs rounded-full px-2 py-0.5">
                          {chat.unread_count}
                        </span>
                      )}
                    </div>

                    <div
                      className={`text-xs truncate mt-1 ${
                        chat.unread_count > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'
                      }`}
                    >
                      {chat.last_message || 'Sem mensagem'}
                    </div>

                    <div className="text-[11px] text-gray-400 mt-1">
                      {formatDateTimeBR(chat.last_message_at)}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="hidden md:flex flex-1 flex-col bg-gray-50">
          {!selectedChat ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <MessageCircle className="w-12 h-12 mb-3 text-gray-300" />
              <p>Selecione uma conversa para visualizar as mensagens.</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b bg-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <UserRound className="w-5 h-5 text-green-700" />
                </div>

                <div>
                  <div className="font-semibold text-gray-900">
                    {selectedChat.patient_name || selectedChat.contact_name || 'Contato não identificado'}
                  </div>

                  {selectedChat.phone && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {selectedChat.phone}
                    </div>
                  )}

                  {selectedChat.conversation_origin === 'multiple_patients_same_user' && (
                    <div className="text-xs text-amber-700 mt-1">
                      Telefone compartilhado, usado por mais de um paciente cadastrado nesta clínica.
                    </div>
                  )}

                  {selectedChat.conversation_origin === 'assigned_from_admin' && (
                    <div className="mt-1 text-xs text-blue-700">
                      Conversa encaminhada pelo Admin do OdontoAssist AI.
                      {selectedChat.admin_forwarded_contact_name
                        ? ` Nome informado no WhatsApp: ${selectedChat.admin_forwarded_contact_name}.`
                        : ''}
                    </div>
                  )}

                  <div className="mt-1">
                    {windowOpen ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-xs">
                        🟢 Janela de atendimento aberta
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs">
                        🟠 Janela de atendimento encerrada
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {loadingMessages ? (
                  <p className="text-sm text-gray-500">Carregando mensagens...</p>
                ) : messages.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhuma mensagem nesta conversa.</p>
                ) : (
                  messages.map((msg) => {
                    const outbound = msg.direction === 'outbound';

                    return (
                      <div
                        key={msg.id}
                        className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                            outbound
                              ? 'bg-green-100 text-gray-900'
                              : 'bg-white text-gray-900 border border-gray-200'
                          }`}
                        >
                          <div className="whitespace-pre-wrap text-left">
                            {msg.text || msg.message_preview || '[Mensagem sem texto]'}
                          </div>
                          <div className="text-[10px] text-gray-400 mt-1 text-right">
                            {formatDateTimeBR(msg.created_at)}
                            {outbound && (
                              <span className="ml-2">
                                {msg.read_status === 'read' || msg.delivery_status === 'read'
                                  ? 'Lida'
                                  : msg.delivery_status === 'delivered'
                                    ? 'Entregue'
                                    : msg.delivery_status === 'failed'
                                      ? 'Falhou'
                                      : 'Enviada'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="p-4 border-t bg-white">
                {windowOpen ? (
                  <>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();

                        const text = replyText.trim();

                        if (!text || !selectedChat?.id || sendingReply) return;

                        try {
                          setSendingReply(true);

                          await axios.post(
                            `${API_URL}/webhooks/whatsapp/chats/${encodeURIComponent(selectedChat.id)}/send`,
                            { text },
                            authHeaders
                          );

                          setReplyText('');
                          await loadMessages(selectedChat, { silent: true });
                          await loadChats();
                        } catch (error) {
                          console.error('Erro ao enviar resposta:', error);
                          alert(error.response?.data?.detail || 'Erro ao enviar mensagem.');
                        } finally {
                          setSendingReply(false);
                        }
                      }}
                      className="flex items-end gap-2"
                    >
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Digite uma mensagem..."
                        rows={2}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />

                      <button
                        type="submit"
                        disabled={sendingReply || !replyText.trim()}
                        className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        {sendingReply ? 'Enviando...' : 'Enviar'}
                      </button>
                    </form>

                    <p className="mt-2 text-[11px] text-gray-500">
                      Respostas livres são permitidas durante até 24 horas após a última mensagem enviada pelo paciente.
                    </p>
                  </>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="font-medium text-amber-800">
                      ⏰ Janela de atendimento encerrada
                    </div>

                    <p className="text-sm text-amber-700 mt-2">
                      O WhatsApp permite responder com mensagens livres apenas durante as 24 horas seguintes à última mensagem enviada pelo paciente.
                    </p>

                    <p className="text-sm text-amber-700 mt-2">
                      Quando o paciente enviar uma nova mensagem para este número, a janela será reaberta automaticamente e você poderá responder normalmente pela Central WhatsApp.
                    </p>

                    <div className="mt-4">
                      <button
                        type="button"
                        disabled
                        className="px-4 py-2 rounded-lg bg-gray-300 text-gray-600 cursor-not-allowed"
                      >
                        Aguardando nova mensagem do paciente
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default WhatsAppBusinessPage;